import { createContext, useCallback, useEffect, useState } from "react";
import { DisbursementColdState, DisbursementFormSteps, LendingStatus, UnderwritingFormStatus, disbursementRoutes } from "../../../interfaces/interfaces"
import { useLocation, useNavigate, useParams } from "react-router";
import { getLocalUser } from "../../../components/functions/utilities";
import { RestS } from "../../../rest/rest-service";
import CircularLoader from "../../../components/common/CircularLoader/CircularLoader";
import styles from './DisbursementFormContext.module.scss';


type DisbursementFormContextType = {
    formData: { [x: string]: any },
    formStepsValidity: { [x: string]: boolean },
    setFormData: (data: any) => void,
    formCompleteStatus: { [x: string]: UnderwritingFormStatus },
    handleAndSetFormStatuses: (formStatuses: any) => void;
    isBtnEnabled: boolean;
    setIsBtnEnabled: (value: boolean) => void;
    formPristine: boolean;
    setFormPristine: (value: boolean) => void;
    setStepValidity: (step: DisbursementFormSteps, isValid: boolean) => void;
}

const disbursementStepRoutes = {
    [DisbursementFormSteps.VERIFY_SUPPLIER]: disbursementRoutes.disbursementVerifySupplier,
    [DisbursementFormSteps.INVOICE_PRODUCTS]: disbursementRoutes.disbursementInvoiceProducts
}

export const DisbursementFormContext = createContext<DisbursementFormContextType>(null);

const DisbursementFormProvider = ({ children }) => {
    const [formData, setFormData] = useState({});
    const [formStepsValidity, setFormStepsValidity] = useState({
        [DisbursementFormSteps.VERIFY_SUPPLIER]: false,
        [DisbursementFormSteps.INVOICE_PRODUCTS]: false,
    });
    const [formCompleteStatus, setFormCompleteStatus] = useState({
        [DisbursementFormSteps.VERIFY_SUPPLIER]: UnderwritingFormStatus.INCOMPLETE,
        [DisbursementFormSteps.INVOICE_PRODUCTS]: UnderwritingFormStatus.INCOMPLETE
    });
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [isBtnEnabled, setIsBtnEnabled] = useState(false);
    const [formPristine, setFormPristine] = useState(false);

    const setStepValidity = useCallback((step: DisbursementFormSteps, isValid: boolean) => {
        console.log('changing step validity', step, "isValid", isValid)
        // formStepsValidityRef.current[step] = isValid;
        setFormStepsValidity({
            ...formStepsValidity,
            [step]: isValid
        })
    }, [formStepsValidity]);

    const currentUser = getLocalUser();
    const { formId } = useParams();

    const handleAndSetFormStatuses = (formStatuses) => {
        const formStatusesObj = formStatuses.reduce((prev, curVal) => {
            prev[curVal.step] = curVal.status;
            return prev;
        }, {});
        setFormCompleteStatus(formStatusesObj);
    }

    const handleInitialVerifiedProducts = (values = {}) => {
        return {
            verifiedProducts: [],
            ...values,
        }
    }

    const handleInitialInvoiceProducts = (values = {}) => {
        return {
            invoiceProducts: [],
            ...values,

        }
    }



    const handleInitialValues = (formValues = {}) => {
        let initialValues = { ...formValues };
        initialValues = handleInitialVerifiedProducts(initialValues)
        initialValues = handleInitialInvoiceProducts(initialValues);
        setFormData({
            ...initialValues
        })
    }

    const { pathname } = useLocation();



    const checkAndSetJourneyStatus = async () => {
        try {

            const { state, success, disbursementData, formSteps } = await RestS.getDisbursementFormData(Number(formId));
            const successStatuses = new Set([LendingStatus.APPROVED, LendingStatus.PENDING_APPROVAL, LendingStatus.REJECTED]);
            if (state == DisbursementColdState.accessDenied) {
                navigate('/404', {
                    replace: true
                })
            } else if (state == DisbursementColdState.linkExpired) {
                // navigate to link expired page
                navigate(`${disbursementRoutes?.disbursementSections}/${disbursementRoutes?.disbursementExpired}/${formId}`, { replace: true })
                return;
            } else {
                // console.log('disbursementFormData', disbursementData)
                if (successStatuses.has(disbursementData['applicationStatus'])) {
                    navigate(`${disbursementRoutes?.disbursementSections}/${disbursementRoutes?.disbursementSuccess}/${formId}`, { replace: true })
                    return;
                } else {
                    handleInitialValues(disbursementData?.data);
                    handleAndSetFormStatuses(formSteps);
                    const productSteps = new Set(Object.keys(disbursementStepRoutes));
                    // const currentRoute = pathname;
                    let isNavigated = false;
                    for (const step of formSteps) {
                        const currentRoute = disbursementStepRoutes[step.step];
                        if (pathname == `/disbursement/${formId}/${currentRoute}`) {
                            navigate(`/disbursement/${formId}/${currentRoute}`, { replace: true });
                            isNavigated = true;
                            break;
                        } else {
                            if (step?.status == UnderwritingFormStatus.INCOMPLETE && productSteps.has(step.step)) {

                                navigate(`/disbursement/${formId}/${currentRoute}`, { replace: true });
                                isNavigated = true;
                                break;
                            }
                        }
                    }
                    if (!isNavigated) {
                        navigate(`/disbursement/${formId}/${disbursementRoutes.disbursementVerifySupplier}`, { replace: true });
                    }
                }
            }
        } catch (error) {
            console.log('error', error);
            throw error;
        }
    }

    useEffect(() => {
        (async () => {
            try {
                await checkAndSetJourneyStatus();
            } catch (error) {
                console.log('error', error)
            } finally {
                setLoading(false);
            }
        })()
    }, [])

    return (
        <DisbursementFormContext.Provider value={{
            formData,
            formStepsValidity,
            setFormData,
            formCompleteStatus,
            handleAndSetFormStatuses,
            isBtnEnabled,
            setIsBtnEnabled,
            formPristine,
            setFormPristine,
            setStepValidity
        }}>
            {loading ?
                <div className={styles.loadingContainer}>
                    <CircularLoader />
                </div> : children}
        </DisbursementFormContext.Provider>
    )

}

export default DisbursementFormProvider;

